// profile.js
//
// Pure, total sanitiser for the user object returned by the login endpoint.
// The server `user` object carries display fields alongside integration
// secrets (`xeroConsent`, `xeroAccessToken`) and may carry a `password`.
// `pickProfile` keeps only the whitelisted non-sensitive display fields so
// nothing sensitive is ever persisted to the Session_Store or localStorage
// (Requirements 4.3, 11.2).
//
// UserProfile (the display-safe subset):
//   {
//     email:       string,
//     first_name:  string,
//     last_name:   string,
//     profile_url: string | null,
//     public_id:   string,
//     admin:       boolean
//   }

// The only fields that may leave the login response and reach persisted state.
// Any other key (password, xeroConsent, xeroAccessToken, ...) is dropped.
const ALLOWED_FIELDS = [
  'email',
  'first_name',
  'last_name',
  'profile_url',
  'public_id',
  // Legacy administrator flag: true for BOTH admin and superadmin.
  'admin',
  // Authoritative role ('employee' | 'admin' | 'superadmin'). Needed to tell an
  // administrator apart from a super administrator.
  'role'
]

/**
 * Reduce a raw user object to the whitelisted non-sensitive display fields.
 *
 * Total and pure: tolerates any input shape (including null/undefined or wrong
 * types), never throws, and never mutates its input. Only keys present in the
 * source and on the whitelist are copied to the result, so credential secrets
 * and integration tokens are always excluded.
 *
 * @param {*} user - the `user` object from the login response (any shape)
 * @returns {{email: string, first_name: string, last_name: string,
 *   profile_url: (string|null), public_id: string, admin: boolean}}
 *   a new object containing only whitelisted fields that exist on the input
 */
export function pickProfile (user) {
  const source = user && typeof user === 'object' && !Array.isArray(user)
    ? user
    : {}

  const profile = {}
  for (const field of ALLOWED_FIELDS) {
    if (Object.prototype.hasOwnProperty.call(source, field)) {
      profile[field] = source[field]
    }
  }

  return profile
}

export default pickProfile
